import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import {
  Dialog,
  FormProvider,
  FormTextField,
  useSnackbarContext,
  useAuth0,
} from "@inductiveautomation/ws-components";

import useLocalizedYupResolver from "../../../hooks/localizedYupResolver";
import { handleResponse } from "../../../utilities/api_response";
import { profileEmailSchema } from "../Profile.schemas";
import { ProfileEmail } from "../Profile.types";
import { usePutPrimaryEmailAddressMutation } from "../api/profile.api";
import {
  retrieveAuthChannel,
  CHANNEL_MESSAGE_TYPES,
} from "../../../utilities/broadcastChannel";

const ChangePrimaryDialog = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [updatePrimaryEmailAddress] = usePutPrimaryEmailAddressMutation();
  const { addSnackbar } = useSnackbarContext();
  const { logout } = useAuth0();
  const { sendAuthChannelMessage } = retrieveAuthChannel();

  const [done, setDone] = useState(false);

  const { t } = useTranslation("translation", {
    keyPrefix: "profile.primary_email.actions",
  });
  const { t: unprefixedT } = useTranslation();

  const resolver = useLocalizedYupResolver(profileEmailSchema);
  const formMethods = useForm<ProfileEmail>({
    resolver,
  });
  const newEmail = formMethods.watch("email");

  const internalOnClose = () => {
    formMethods.reset();
    setDone(false);
    onClose();
  };

  if (done) {
    return (
      <Dialog
        testId="change-primary-email-address-success"
        isOpen={isOpen}
        onClose={internalOnClose}
        headline={t("change_success.heading")}
        supportingText={t("change_success.description", { newEmail })}
        showCloseButton
      />
    );
  }

  const onSubmitForm = formMethods.handleSubmit((data: ProfileEmail) => {
    void updatePrimaryEmailAddress({
      emailAddressPayload: { emailAddress: data.email },
    }).then((response) => {
      handleResponse(response, {
        onSuccess: () => {
          setDone(true);
          addSnackbar(t("change.success_message"));
          setTimeout(() => {
            void logout({
              logoutParams: {
                returnTo: window._acctConfig?.AUTH0_LOGOUT_URL,
              },
            }).then(() => {
              sendAuthChannelMessage(CHANNEL_MESSAGE_TYPES.LOGOUT);
            });
          }, 2000);
        },
        onError: () => {
          addSnackbar(t("change.error_message"), {
            type: "error",
          });
        },
        fallbackErrorMessage: t("change.error_message"),
      });
    });
  });

  return (
    <Dialog
      testId="change-primary-email-address"
      isOpen={isOpen}
      onClose={internalOnClose}
      headline={t("change.heading")}
      supportingText={t("change.description")}
      confirmOnClose={!!newEmail}
      collapseContentScrollGutter
      contentArea={
        <FormProvider {...formMethods}>
          <form onSubmit={(e) => void onSubmitForm(e)}>
            <FormTextField
              prefix="profile"
              name="email"
              label={t("change.form.email_address")}
              autoFocus
            />
          </form>
        </FormProvider>
      }
      actions={[
        {
          label: unprefixedT("common.cancel"),
          type: "cancel",
        },
        {
          label: t("change.form.submit_label"),
          type: "submit",
          disabled: !newEmail,
          onClick: () => {
            void onSubmitForm();
          },
        },
      ]}
      showCloseButton
    />
  );
};

export default ChangePrimaryDialog;
